// @ts-strict-ignore
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import {ParamComponent} from 'panel';
import {NavigateFunction} from 'react-router';

import {
  CurrentProject,
  CurrentProjectResource,
  Project,
  ProjectsResource,
} from 'common/report/model-resources/projects';
import {CurrentUser, CurrentUserResource} from 'common/report/model-resources/users';
import {currentProjectStore} from 'common/report/stores/project-details';
import {supportInfoStore} from 'common/report/stores/support-info';
import {
  closestReportLink,
  debug,
  excludeDemos,
  getDownloadMode,
  getPersistedSearch,
  getProjectId,
  getReportName,
  getReportUrl,
  getWorkspaceId,
  isPersistableReport,
} from 'common/report/util';
import {spaNavManager} from 'common/report/util/spa-nav-manager';
import {WorkspaceObj, workspacesClient} from 'common/report/queries/app-api/workspaces-client';
import {
  getUserFlags,
  incrementUserFlag,
  setUserFlag,
  unsetUserFlag,
  viewedReportFlagManager,
} from 'common/report/queries/app-api/user-flags';
import {getProductUpdates, markProductUpdatesAsSeen} from 'common/report/queries/app-api/product-updates-client';
import {events, properties, track} from 'common/report/util/settings-tracking';
import {isProjectOnlyApp, makePathFromUrlState, parseStateFromUrl} from 'common/report/util/state-from-url';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {capitalize, nameToInitials, pluralize, truncateMiddle} from 'common/util/string';
import {getCookie, safeSetCookie} from 'common/util/cookie';
import {generateShortcutLabel, getCommandKey, keyEventSwitcher} from 'common/util/keyboard';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {Model, ModelStatus} from 'common/util/model-resource/model-resource';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  ApiFailureDescription,
  sortWorkspaces,
  UISourceObject,
  workspaceApiFailure,
  workspaceCreateEvent,
  workspacesTrackingPrefix,
} from 'common/util/workspaces';
import {promptManageWorkspaceModal} from 'common/widgets/create-entity-modal';
import {LabelTheme, Section, SectionItem, SubLabelTheme} from 'common/components/items-menu/types';
import {
  DEFAULT_SEARCH_QUERY_STRING,
  getBrowseUrl,
  GetBrowseUrlOptions,
  getSearchContextFromReportName,
} from 'common/report/util/search';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  SearchPhaseVersion,
  SearchSource,
  SearchTrackedProperty,
} from 'common/report/util/search/tracking';
import {SwitchEl} from 'common/components/switch';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {reporter} from 'common/report/error-reporting';
import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';
import {
  DownloadMode,
  LocalStorageKey,
  Permission,
  ProjectFeatureFlag,
  ProjectType,
  REPORT_PERSISTENCE_COOKIE,
  ReportName,
  UserFlag,
} from 'common/report/constants';
import {
  activatePersistedZendeskChat,
  activateZendeskChat,
  ChatDepartment,
  hideZendeskWidget,
} from 'common/report/external-widgets/zendesk';
import {UserIconEl} from 'common/widgets/user-icon';
import {IconSizeType} from 'common/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {shouldOpenInNewTab} from 'common/util/dom';
import {promptFeedbackModal} from 'common/widgets/feedback-modal';
import {FeedbackForFeature} from 'common/widgets/feedback-modal/types';
import {captureWorkflowTiming, QueryStartEvent} from 'common/util/workflow-timing';
import {userDetailsStore} from 'common/report/stores/user-details';
import {userFlagsStore} from 'common/report/stores/user-flags';
import {featureSamplingUtils} from 'common/report/util/feature-sampling';
import {CreditBalanceInfo, CreditInfo} from 'common/report/queries/app-api/project-metadata-client';
import {promptFYTModal} from 'common/widgets/find-your-team-modal';
import {promptIntroModal} from 'common/widgets/intro';
import {initImmersiveOnboardingInitializer} from 'common/onboarding/immersive-onboarding-initializer';
import {Step} from 'common/onboarding/immersive-onboarding/types';
import {IMMERSIVE_ONBOARDING_URL_PARAM} from 'common/onboarding/immersive-onboarding/constants';
import {useStore} from 'common/util/component-helpers';
import {changeLanguage} from 'common/i18n';
import {getLocation} from 'common/util/url';
import {getLocaleMenuSections} from 'common/widgets/language-selector';
import {Organization} from 'common/report/queries/app-api/user-metadata-client';
import {isGlobalSidenavEnabled} from 'iron/routes/global-sidenav/utils';
import {SupportWidgetAttrs} from 'common/widgets/chrome/chrome-header/support-widget';
import {SubscribableContext} from 'common/util/model-resource/subscribable-context';

import {NavigationTrackingEvent} from './reports-menu/tracking';
import {
  AppMenuApp,
  ChromeHeaderState,
  HelpOnboardingSectionItemValue,
  HelpProductUpdateSectionItemValue,
  LastUserProjectState,
  NavCategories,
  NavReports,
  PersistedDarkModeState,
  ReportContext,
  ReportPersistenceCookie,
} from './types';
import {
  DARK_MODE_KEY,
  FULL_CHANGELOG_LINK,
  HelpGettingStartedLabels,
  HelpLabels,
  HelpLabelTranslatables,
  HelpSectionItemType,
  HelpTrackingEvent,
  LAST_USER_PROJECT_KEY,
  NavCategoryType,
  REPORT_INFO,
  SETTINGS_CATEGORY_TRACKING,
} from './constants';
import {
  getAccountSettingsMenuItem,
  getFilteredOrgs,
  getNavCategories,
  getNavReportsFromCategory,
  getOrgSettingsMenuItem,
  getProductUpdateSections,
  getProjectSettingsMenuItem,
  getStartupCreditPromoSectionItem,
  logout,
  warmUsageAnalyticsData,
} from './util';
import {template} from './template';
import css from './index.inline.styl';
import './reports-menu';

const TOAST_TTL = 7000;
const toastCtaCallbackMap = new Map<string, () => void>();

interface NavigateOptions {
  fromAnchorlink?: boolean;
  keepReportContext?: boolean;
}

function isHelpOnboardingItem(item: SectionItem<any>): item is SectionItem<HelpOnboardingSectionItemValue> {
  return item.value?.type === HelpSectionItemType.GettingStarted;
}

function isHelpProductUpdateItem(item: SectionItem<any>): item is SectionItem<HelpProductUpdateSectionItemValue> {
  return item.value?.type === HelpSectionItemType.ProductUpdate;
}

function isSavedCoreReportUrl(url: string) {
  if (!/^https?:/.test(url)) {
    url = `${getLocation().protocol}//${getLocation().hostname}${url}`;
  }

  const {pathname, hash} = new URL(url);
  const state = parseStateFromUrl(pathname);

  if (![`insights`, `funnels`, `flows`, `retention`].includes(state.appName)) {
    return false;
  }

  return /^#(view|report)\/\d+/.test(hash);
}

export enum HeaderMode {
  'Project' = 1,
  'Organization' = 2,
  // theoretically we could have a "None" option, methinks
}

type ChromeHeaderParams = {
  hidden?: boolean;
  navigate?: NavigateFunction;
  headerMode?: HeaderMode;
};

type JsxProps = {
  params: ChromeHeaderParams;
};

export class ChromeHeader extends ParamComponent<ChromeHeaderParams, ChromeHeaderState, IronContextRegistry> {
  private declare lastUserProjectStorage: UserLocalStorageHelper<LastUserProjectState>;
  private declare userLocalStorageDarkModeHelper: UserLocalStorageHelper<PersistedDarkModeState>;
  private startupCreditPromoFeatureEligible: boolean | null = null;

  get config() {
    const defaultState: ChromeHeaderState = {
      categories: null,
      createProjectError: null,
      createProjectName: null,
      currentCategoryName: ``,
      currentUrlPath: ``,
      currentReportName: ``,
      errorAlertMsg: null,
      inviteModalOrgs: [],
      isAtProjectMax: false,
      isNewUser: false,
      isOpen: {
        applicationsMenu: false,
        createProjectModal: false,
        helpMenu: false,
        inviteModal: false,
        searchModal: false,
        projectMenu: false,
        searchMenu: false,
        setupMenu: false,
        userMenu: false,
      },
      // Only used to control rendering logic of onboarding checklist
      isStarterBoardModalOpen: false,
      isSupportWidgetMaximized: false,
      projectId: null,
      projectMenuWorkspaces: [],
      productUpdates: [],
      searchedText: DEFAULT_SEARCH_QUERY_STRING,
      selectedCategory: ``,
      showNewProjectInput: false,
      showSupportWidget: false,
      showChangelogModal: false,
      viewedReportCount: 0,
      viewedStartupCreditPromo: false,
      dismissedOnboardingChecklist: false,
      enableOnboardingChecklist: false,
      showOnboardingChecklistHelpTooltip: false,
      enableImmersiveOnboarding: false,
      closeInviteModalCallback: null,
    };

    return {
      css,
      template,
      useShadowDom: true,
      defaultState,
      params: {
        hidden: Boolean,
        navigate: Function,
        headerMode: Number,
      },
      contexts: [
        `localStorageManager` as const,
        `urlManager` as const,
        `queryContextManager` as const,
        `currentProject` as const,
        `currentUser` as const,
        `projects` as const,
        `i18n` as const,
        `themes` as const,
        `tours` as const,
      ],
      hooks: {
        preUpdate: (state) => {
          const {enableImmersiveOnboarding} = state || {};
          if (enableImmersiveOnboarding && !this.state.enableImmersiveOnboarding) {
            setTimeout(() => {
              initImmersiveOnboardingInitializer();
            }, 500);
          }
        },
      },
      helpers: {
        stickyElemHooks,

        handleChangelogModalClose: () => this.update({showChangelogModal: false}),
        currentProjectStoreHasValue: () => !!this.getCurrentProject(),
        getLastUserProjectVisitedId: (): number | undefined => {
          return (
            (this.lastUserProjectStorage &&
              this.lastUserProjectStorage.getItem({
                key: LAST_USER_PROJECT_KEY,
                userId: userCookieUtils.getUserIdentityFromCookie()?.id,
              })?.projectId) ||
            undefined
          );
        },
        hasProjectFeatureFlag: (flag: ProjectFeatureFlag) => this.getCurrentProject()?.hasFeatureFlag(flag) ?? false,
        isDemoProject: () => this.getCurrentProject()?.isDemo ?? true, // Assume restrictive access
        shouldDisplayNavigationOptions: () => {
          if (isGlobalSidenavEnabled(this.getCurrentProject())) {
            return false;
          }
          if (this.params.headerMode === HeaderMode.Organization) {
            return false;
          }
          const {projectId, currentReportName, categories} = this.state;
          // this happens when user has first signed up and they don't have any projects to navigate to
          // and when a user is viewing a page outside of a project context (e.g. Admin Org settings)
          if (!projectId) {
            return false;
          }
          // don't show navigation options if we don't have categories to show
          if (!categories) {
            return false;
          }

          const workspaceId = this.currentWorkspaceId;

          // hides navigation options when:
          // 1. a user is in Data View library but we can't infer a workspaceId
          // 2. a user does not belong to any data views
          // when a project contains workspaces, we should always navigate to workspace based reports
          return !(currentReportName === `views` && workspaceId === null);
        },

        allMenusClosed: () => !Object.values(this.state.isOpen).find((menu) => menu),

        updateOpen: (updated?: ChromeHeaderState[`isOpen`]) => {
          if (updated) {
            this.update({isOpen: {...this.state.isOpen, ...updated}});
          } else {
            // if not provided a specific update, just close everything
            this.update({
              isOpen: Object.keys(this.state.isOpen).reduce((o, key) => Object.assign(o, {[key]: false}), {}),
            });
          }
        },

        getProjectAndWorkspaceName: (truncated: boolean) => {
          const projectName = this.getCurrentProjectName();
          const workspaceName =
            this.helpers.areWorkspacesActivated(this.getCurrentProject()?.id) &&
            (this.helpers.getCurrentWorkspace()?.name || ``);
          const separator = ` · `;

          if (truncated) {
            // maxCharLen constant accommodates the project selector such that chrome-header doesn't overflow 1150px.
            // 1150px is the minimum viewport width we support defined in global.styl
            const maxCharLen = 46;
            if (workspaceName) {
              const truncatedWorkspaceName = truncateMiddle(workspaceName, maxCharLen / 2);
              const remainingLen = maxCharLen - truncatedWorkspaceName.length - separator.length;
              const truncatedProjectName = truncateMiddle(projectName, remainingLen);
              return truncatedProjectName + separator + truncatedWorkspaceName;
            } else {
              return truncateMiddle(projectName, maxCharLen);
            }
          } else {
            return workspaceName ? projectName + separator + workspaceName : projectName;
          }
        },

        getProjectItems: (projects: Project[]): SectionItem[] =>
          projects
            .map((proj) => {
              const workspaceCount = this.getWorkspacesForProject(proj.id).length;
              const workspaceOverrides = this.helpers.areWorkspacesActivated(proj.id)
                ? {
                    subLabel: `${workspaceCount} ${pluralize(`Data View`, workspaceCount)}`,
                    hasCaret: workspaceCount > 0,
                  }
                : {};

              return {
                label: proj.name || `My New Project`,
                subLabel:
                  proj.isRollupProject() && this.helpers.getOrgs().length > 1
                    ? this.t(`nav-header.rollup-projects`)
                    : ``,
                projectId: proj.id,
                type: proj.type,
                ...workspaceOverrides,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label)),

        selectProjectMenuBackClick: () => this.update({projectMenuWorkspaces: []}),

        getProjectsForMenu: (): Project[] => excludeDemos(this.getAllProjects()),
        getProjectsMenuSections: (): Section[] => {
          let orgList;
          const allProjects = this.helpers.getProjectsForMenu();
          const projectMenuWorkspaces = this.state.projectMenuWorkspaces;
          let sections: Section[];

          if (this.helpers.areWorkspacesActivated()) {
            sections = [
              {
                items: projectMenuWorkspaces.map((workspace) => ({
                  label: workspace.name,
                  id: workspace.id,
                  isWorkspace: true,
                  projectId: workspace.projectId,
                  tooltip: {
                    text: `${workspace.name}\n${this.t(`nav-header.data-view-created-by`, {
                      creator: workspace.creator,
                    })}\n\n${workspace.description}\n[${this.t(
                      `nav-header.data-view-more-details`,
                    )}](/settings/project/${workspace.projectId}/views/detail/${workspace.id})`,
                    size: DisplaySize.Medium,
                  },
                })),
              },
              {
                items: [
                  {
                    label: this.t(`nav-header.data-view-library`),
                    icon: `open`,
                    projectId: projectMenuWorkspaces[0].projectId,
                    href: getReportUrl(`views`, {projectId: projectMenuWorkspaces[0].projectId}),
                  },
                ],
              },
            ];
          } else if ((orgList = this.helpers.getOrgs({excludeDemoOrgs: true})).length > 1) {
            sections = orgList.map((org) => ({
              label: orgList.length > 1 ? org.name : null,
              items: this.helpers.getProjectItems(allProjects.filter((p: Project) => p.organizationId === org.id)),
            }));
          } else {
            const projects = allProjects.filter((p) => !p.isRollupProject());
            const rollupProjects = allProjects.filter((p) => p.isRollupProject());
            const hasRollupProjects = rollupProjects.length > 0;
            sections = [
              {
                // Only explicitly label if there are rollup projects, otherwise its just a single list
                label: hasRollupProjects ? this.t(`nav-header.projects`) : ``,
                items: this.helpers.getProjectItems(projects),
              },
            ];

            if (hasRollupProjects) {
              sections.push({
                label: this.t(`nav-header.rollup-projects`),
                items: this.helpers.getProjectItems(rollupProjects),
              });
            }
          }
          if (!this.helpers.areWorkspacesActivated()) {
            sections.push({
              label: this.t(`nav-header.mixpanel-demo`),
              items: [
                {
                  label: this.t(`nav-header.sample-datasets`),
                  icon: `open`,
                  projectId: 0,
                  onClick: () => {
                    openDemoProjectSelectorModal((ev) => {
                      this.addToast(ev.detail.message, ToastType.ERROR);
                    });
                  },
                  trackEvent: `[Demo Project] Click Explore Demo`,
                  trackProps: {'Navigated From': `Project Menu`},
                },
              ],
            });
          }

          // cap sections to max of 10 items so list isn't insanely long
          for (const section of sections) {
            section.showLimit = section.items.length > 10 ? 5 : 10;
          }

          return sections;
        },

        getSearchModalDisplayContext: (): SearchDisplayContext => {
          const currentReportName = this.state.currentReportName;
          if (
            [
              ReportName.Dashboards,
              ReportName.Flows,
              ReportName.Funnels,
              ReportName.Insights,
              ReportName.Retention,
              ReportName.Impact,
            ].includes(currentReportName as ReportName)
          ) {
            return getSearchContextFromReportName(currentReportName as ReportName);
          }
          return null;
        },

        handleOpenReportsAndDashboardClicked: () => {
          this.helpers.updateOpen({searchModal: true});
          mixpanel.track(`[Header] Search Report Clicked`);
        },

        getReportLink: () => {
          const {currentReportName} = this.state;
          switch (currentReportName) {
            case ReportName.Dashboards:
              return `https://docs.mixpanel.com/docs/boards/overview`;
              break;
            // TODO: investigate this const because we might have name dependency on "dashboards"
            case `boards`:
              return `https://docs.mixpanel.com/docs/boards/overview`;
              break;
            case ReportName.Insights:
              return `https://docs.mixpanel.com/docs/reports/${currentReportName}`;
              break;
            case ReportName.Funnels:
              return `https://docs.mixpanel.com/docs/reports/${currentReportName}`;
              break;
            case ReportName.Retention:
              return `https://docs.mixpanel.com/docs/reports/${currentReportName}`;
              break;
            case ReportName.Flows:
              return `https://docs.mixpanel.com/docs/reports/${currentReportName}`;
              break;
            case ReportName.Impact:
              return `https://docs.mixpanel.com/docs/reports/apps/impact`;
              break;
            case ReportName.Signal:
              return `https://docs.mixpanel.com/docs/reports/apps/signal`;
              break;
            case ReportName.ExperimentReporting:
              return `https://docs.mixpanel.com/docs/reports/apps/experiments`;
              break;
            case ReportName.Settings:
              return `https://docs.mixpanel.com/docs/orgs-and-projects/overview`;
              break;
            case ReportName.Lexicon:
              return `https://docs.mixpanel.com/docs/data-governance/lexicon`;
              break;
            default:
              return `https://docs.mixpanel.com`;
              break;
          }
        },

        getHelpMenuSections: (): Section[] => {
          const {supportChatEnabled} = supportInfoStore.value;
          const productUpdateSections = getProductUpdateSections(this.state.productUpdates);
          const firstSectionItems = [
            {
              label: HelpLabelTranslatables[HelpLabels.Docs],
              value: HelpLabels.Docs,
              href: this.helpers.getReportLink(),
              target: `_blank`,
            },
            {
              label: HelpLabelTranslatables[HelpLabels.DemoProject],
              value: HelpLabels.DemoProject,
            },
          ];

          // if data is fetched, show the modal, else the menu item should open docs changelog page
          if (productUpdateSections) {
            firstSectionItems.splice(1, 0, {
              label: HelpLabelTranslatables[HelpLabels.ProductUpdates],
              value: HelpLabels.ProductUpdates,
            });
          } else {
            firstSectionItems.splice(1, 0, {
              label: HelpLabelTranslatables[HelpLabels.ProductUpdates],
              value: HelpLabels.ProductUpdates,
              href: FULL_CHANGELOG_LINK,
              target: `_blank`,
            });
          }

          return [
            {
              items: firstSectionItems,
            },
            {
              items: (this.getCurrentProject()?.externalHelpLinks || []).map((link) => ({
                label: link.linkName,
                href: link.linkURL,
                target: `_blank`,
                isDisabled: !link.linkName,
              })),
            },
            {
              items: [
                {
                  label: HelpLabelTranslatables[HelpLabels.SubmitFeedback],
                  value: HelpLabels.SubmitFeedback,
                },
                supportChatEnabled && {
                  label: HelpLabelTranslatables[HelpLabels.LiveChat],
                  value: HelpLabels.LiveChat,
                },
                !this.isGuestUser && {
                  label: HelpLabelTranslatables[HelpLabels.ContactSupport],
                  value: HelpLabels.ContactSupport,
                },
                {
                  label: HelpLabelTranslatables[HelpLabels.ContactSales],
                  value: HelpLabels.ContactSales,
                },
              ].filter(Boolean),
            },
          ];
        },

        /** areWorkspacesActivated a boolean to indicate the project has actually started to utilize workspaces[dataviews] */
        areWorkspacesActivated: (projectId?: number) => {
          const projectMenuWorkspaces = !projectId
            ? this.state.projectMenuWorkspaces
            : this.getWorkspacesForProject(projectId);

          return (
            projectMenuWorkspaces.length > 1 ||
            (projectMenuWorkspaces.length === 1 && this.getProject(projectMenuWorkspaces[0].projectId)?.hasWorkspaces)
          );
        },
        getCurrentWorkspace: () => this.getCurrentProject()?.workspaces[this.currentWorkspaceId],

        getUrlForProject: (projectId: number) => {
          const workspaceId = get(this.getWorkspacesForProject(projectId)[0], `id`);
          return makePathFromUrlState({projectId: `${projectId}`, workspaceId: `${workspaceId}`});
        },

        updateCurrentCategoryAndReportNameFromUrl: () => {
          const location = this.getUrlManager().getLocation();
          const reportInfo = this.getReportInfoFromUrl(new URL(location.href));
          if (reportInfo) {
            this.update({
              currentCategoryName: reportInfo.categoryName,
              currentReportName: reportInfo.reportName,
            });
            this.persistLastSeenUrlData();
          }
        },

        hasOpenModal: (): boolean => {
          return Object.keys(this.state.isOpen).some((k) => this.state.isOpen[k]);
        },

        initializeSpaNavManager: () => {
          const currentProject = this.getCurrentProject();
          if (!currentProject) {
            return;
          }
          // multiple async entry points, use the one that's ready
          spaNavManager.initialize({
            projectId: String(this.state.projectId),
            projectName: currentProject.name,
            navigate: this.params.navigate,
          });
        },

        // TODO
        navigate: (
          url: string,
          ev = null,
          {fromAnchorlink = false, keepReportContext = false}: NavigateOptions = {},
        ) => {
          const urlObj = new URL(url, getLocation().origin);
          urlObj.search = getPersistedSearch(getLocation().search);
          const destinationUrl = urlObj.toString();
          const {currentUrlPath} = this.state;

          // If we are keeping context between reports we must navigate via spa nav.
          if (keepReportContext) {
            this.helpers.initializeSpaNavManager();
          }
          if (!keepReportContext) {
            this.getContext(`queryContextManager`).reset();
          }

          if (shouldOpenInNewTab(ev)) {
            // if anchor link, browser will do the right thing by opening in new tab anyway
            if (!fromAnchorlink) {
              window.open(destinationUrl, `_blank`);
            }
          } else {
            if (spaNavManager.isNavigable(destinationUrl, currentUrlPath)) {
              // this will be a fake page navigation by swapping the iron app js bundle and css
              // we'll ask the window's beforeunload whether we should ask the user to stay on the page
              let shouldNavigate = true;
              if (window.onbeforeunload instanceof Function) {
                const beforeUnloadEv = new CustomEvent(`beforeunload`, {cancelable: true});
                window.onbeforeunload(beforeUnloadEv);
                if (beforeUnloadEv.defaultPrevented) {
                  shouldNavigate = confirm(`Changes you made may not be saved. Leave this page?`);
                }
              }

              if (shouldNavigate) {
                spaNavManager.navigate(destinationUrl, currentUrlPath, ev, keepReportContext);
                this.helpers.updateCurrentCategoryAndReportNameFromUrl();
                this.update({currentUrlPath: destinationUrl});
              } else {
                // user said no, don't navigate
                if (ev) {
                  if (ev.type === `popstate`) {
                    // popstate event immediately overwrites the url even though the user didn't want to leave
                    // push back the old url
                    window.history.pushState(null, null, currentUrlPath);
                  } else {
                    ev.preventDefault();
                  }
                }
              }
            } else {
              this.update({currentUrlPath: destinationUrl});

              // if an anchor link is clicked, let the browser do it's thing
              // otherwise onbeforeunload will fire twice if we also set location.href
              // see https://mixpanel.atlassian.net/browse/RNA-737
              if (!fromAnchorlink) {
                window.location.href = destinationUrl;
              }

              const handleBeforeUnload = (beforeUnloadEv: BeforeUnloadEvent) => {
                // ev.defaultPrevented == true means the user was prompted to stay or leave.
                // we have no way to know what their choice was
                // so don't do an optimistic report whiteout and select the new report tab
                // if user leaves, the right state will be shown anyway after the page loads, just a bit slower
                if (!beforeUnloadEv.defaultPrevented) {
                  // optimistic update that the new page started loading instantly
                  // by showing whiteout loader spinny and selecting the new report in nav
                  this.helpers.updateCurrentCategoryAndReportNameFromUrl();
                  document.querySelector(`.whiteout_loader`)?.classList.remove(`hidden`);
                }
              };

              window.addEventListener(`beforeunload`, handleBeforeUnload, {once: true});
            }
          }
        },

        handleHistoryStateChange: (ev: Event) => {
          this.helpers.updateCurrentCategoryAndReportNameFromUrl();

          // GOOT for Lexicon reloading on back button
          // TODO (Sonya) remove this handler - one off logic to unblock Lexicon rollout
          if (getReportName() === ReportName.Lexicon) {
            return;
          }

          if (ev.type === `popstate`) {
            // NOTE: popstate is fired for history forward/back and location hash changes
            // we only navigate if the pathname changes, for hash only changes, let the panel router handle changes
            // otherwise we'll get a 'Maximum call stack size exceeded' error as navigate() causes another popstate
            const prevPathname = new URL(this.state.currentUrlPath, getLocation().origin).pathname;
            const location = this.getUrlManager().getLocation();
            const curPathname = location.pathname;
            if (curPathname !== prevPathname) {
              this.helpers.navigate(curPathname + location.hash, ev);
            }
          }
        },

        selectProjectMenuItem: (ev) => {
          const {item} = ev.detail;
          if (item.projectId) {
            this.helpers.navigateToProject(ev);
          }
          if (item.href || item.onClick) {
            // track, call onClick, and navigate in that order,
            // so that all three can be done if need be
            if (item.trackEvent) {
              mixpanel.track(item.trackEvent, item.trackProps || {});
            }
            if (item.onClick) {
              item.onClick();
            }
            if (item.href) {
              this.helpers.navigate(item.href, ev, {fromAnchorlink: true});
            }
          }
        },

        navigateToBoard: (dashboardId: number, projectId: number, workspaceId?: number): void => {
          const wsId = workspaceId || this.getWorkspacesForProject(projectId)[0]?.id || undefined;
          const targetUrl = getReportUrl(`boards/#id=${dashboardId}`, {
            projectId,
            workspaceId: wsId,
          });
          return this.helpers.navigate(targetUrl);
        },

        navigateToProject: (ev) => {
          const {item} = ev.detail;
          const projectId = item.projectId;
          const projectDomain = this.getCurrentProject()?.domain;
          const workspaceId = item.isWorkspace ? item.id : get(this.getWorkspacesForProject(projectId)[0], `id`);
          const location = this.getUrlManager().getLocation();

          if (item.hasCaret) {
            const projectMenuWorkspaces = this.getWorkspacesForProject(projectId);
            if (projectMenuWorkspaces.length) {
              this.update({projectMenuWorkspaces});
              return;
            }
          }

          this.helpers.updateOpen({projectMenu: false});
          this.update({projectMenuWorkspaces: []});

          // e.g if user is in insights, navigate to insights in new project or workspace
          let navigatePath = makePathFromUrlState({projectId, workspaceId});

          // if switching between workspaces for the same project, preserve the hash for reports to keep selected user report
          if (
            navigatePath &&
            `${workspaceId}` !== `${this.currentWorkspaceId}` &&
            `${projectId}` === `${this.state.projectId}`
          ) {
            navigatePath += location.hash;
          }

          if (!isPersistableReport() || !navigatePath) {
            // let django navigate to last persisted report
            // trailing slashes are important otherwise we get an extra 301 roundtrip
            navigatePath = workspaceId ? `/report/${projectId}/view/${workspaceId}/` : `/report/${projectId}/`;
          }

          if (workspaceId) {
            const allProjectWorkspaces = this.getWorkspacesForProject(projectId);
            const hasGlobalWorkspaceAccess = allProjectWorkspaces.some((w) => w.isGlobal);
            const hasDefaultWorkspaceAccess = allProjectWorkspaces.some((w) => w.isDefault);
            const selectedWorkspace: WorkspaceObj = allProjectWorkspaces.find(
              (w: WorkspaceObj) => w.id === workspaceId,
            );
            mixpanel.track(`Selected Workspace`, {
              'Selected Project ID': projectId,
              'Selected Workspace ID': workspaceId,
              'Selected Workspace Name': ev.detail.item.label,
              'Selected Workspace Is Global': selectedWorkspace.isGlobal,
              'Selected Workspace Is Default': selectedWorkspace.isDefault,
              'User Has Global Workspace Access': hasGlobalWorkspaceAccess,
              'User Has Default Workspace Access': hasDefaultWorkspaceAccess,
              'Total Available Workspaces': allProjectWorkspaces.length,
              // track url pattern e.g /report/<pid>/view/<wid>/insights. remove trailing slashes
              'Navigate Url Pattern': navigatePath
                .replace(projectId, `<pid>`)
                .replace(workspaceId, `<wid>`)
                .replace(/\/$/, ``),
            });
          } else {
            mixpanel.track(`Selected Project`, {
              'Selected Project ID': projectId,
              'Selected Project Name': ev.detail.item.label,
              'Selected Project Type': ev.detail.item.type === ProjectType.Rollup ? `Rollup Project` : `Project`,
              // track url pattern e.g /report/<pid>/insights. remove trailing slashes
              'Navigate Url Pattern': navigatePath.replace(projectId, `<pid>`).replace(/\/$/, ``),
            });
          }

          // This logic only applies when we are explicitly switching projects and not report types,
          // so we keep it out of helpers.navigate to avoid an adding another argument to a complex function.
          // we only want to switch domains for staging and prod i.e (x.)?mixpanel.com domains,
          // not for devboxes or localhost standalone mode
          const curDomain = getLocation().hostname;
          const isMixpanelDotCom = (domain: string) => /\bmixpanel\.com$/.test(domain);
          if (isMixpanelDotCom(curDomain) && projectDomain && projectDomain !== curDomain) {
            this.getUrlManager().setLocation(`//` + projectDomain + navigatePath);
          } else {
            this.helpers.navigate(navigatePath, ev);
          }
        },

        clickedCreateProject: () => {
          this.helpers.updateOpen({createProjectModal: true, projectMenu: false});
        },

        updateCreateProjectName: (ev) => this.update({createProjectName: ev.target.value}),

        clickedCancel: () => this.helpers.updateOpen({createProjectModal: false}),

        projectNameValid: () => Boolean(this.state.createProjectName) && Boolean(this.state.createProjectName.trim()),

        clickedCreate: (ev) => {
          try {
            // comes from CreateProjectModalEl
            if (ev?.detail?.error) {
              throw new Error(ev.detail.error);
            }
            const newProjectId = ev?.detail?.response?.id;
            this.helpers.navigate(`/report/${newProjectId}/`);
          } catch (error) {
            this.update({createProjectError: error});
          }
        },

        getOrgsWithCreateProjectPermission: () => this.helpers.getOrgs({permissions: [Permission.CreateProject]}),

        orgHasPerm: (org, perm) => this.orgHasPerm(org, perm),

        orgIdForCreateProject: () => this.helpers.getOrgsWithCreateProjectPermission()[0]?.id,
        userInitials: () => nameToInitials(this.getCurrentUser()?.name),

        getOrgs: ({permissions = null, excludeDemoOrgs = false} = {}) => {
          return getFilteredOrgs(this.getCurrentUser()?.organizations || userDetailsStore.value?.organizations, {
            permissions,
            excludeDemoOrgs,
          });
        },

        hasOrgs: () => !isEmpty(this.getCurrentUser()?.organizations),

        // warm up the slowest and most often used usage analytics charts when a user clicks on the cog.
        warmUsageAnalyticsData: () => {
          warmUsageAnalyticsData({
            currentProject: this.getCurrentProject(),
            currentUser: this.getCurrentUser(),
          });
        },

        menuItemSelected: (ev) => {
          const {item}: {item: SectionItem} = ev.detail;
          if (item.href) {
            this.trackSettingsMenuItemSelected(item);
            if (item.href === `/pricing/plan-builder`) {
              this.setViewedStartupCreditPromo();
              track(SETTINGS_CATEGORY_TRACKING.StartupCreditPromoClicked, {'Navigated From': `Settings Menu`});
            }
          } else if (item.locale) {
            // show localization tour if user changes to any language
            if (item.locale !== `en`) {
              this.getContext(`tours`).openTour(`localization`);
            }

            mixpanel.track(events.userSetLanguage, {language: item.locale});

            changeLanguage(item.locale);
          } else {
            switch (item.value) {
              case `settings.leave-demo`:
              case `settings.logout`: {
                logout({localStorageManager: this.getContext(`localStorageManager`)});
                break;
              }
              case `settings.invite-users`:
                this.helpers.openInviteUserModal();
            }
          }
        },

        openInviteUserModal: (closeCallback?) => {
          const orgUrlMatch = /^\/settings\/org\/(\d+)/.exec(location.pathname);
          const orgId = parseInt(orgUrlMatch && orgUrlMatch[1], 10) || null;
          const orgs = this.helpers.getOrgs({permissions: [Permission.AddUser]});
          const org = orgs.find((o) => o.id === orgId);
          // if org id is specified by url and that org has `add_user` permission, show
          // invite modal for that org; otherwise show modal for all orgs with `add_user`
          // (user will choose org in modal)
          const inviteModalOrgs = org ? [org] : orgs;
          if (inviteModalOrgs.length) {
            this.helpers.updateOpen({inviteModal: true});
            this.update({inviteModalOrgs, closeInviteModalCallback: closeCallback});

            track(SETTINGS_CATEGORY_TRACKING.InviteModal, {
              'Navigated From': `Shortcut`,
            });
          }
        },

        closeSupportWidget: () => {
          this.update({showSupportWidget: false});
        },

        helpMenuItemSelected: (ev) => {
          const {item} = ev.detail;
          // Handle "Getting Started" and "Product Update" subsections.
          if (isHelpOnboardingItem(item) || isHelpProductUpdateItem(item)) {
            const {helpLink} = item.value;

            const trackingEvent = isHelpOnboardingItem(item)
              ? HelpTrackingEvent.GettingStarted
              : HelpTrackingEvent.WhatsNew;

            mixpanel.track(trackingEvent, {
              'help link': helpLink,
              label: item.label,
            });

            window.open(helpLink, `_blank`);

            return;
          }

          switch (item.value) {
            case HelpLabels.DemoProject:
              openDemoProjectSelectorModal((openDemoProjectEvent) => {
                this.addToast(openDemoProjectEvent.detail.message, ToastType.ERROR);
              });
              mixpanel.track(`[Demo Project] Click Explore Demo`, {
                'Navigated From': `Project Menu`,
              });
              break;
            case HelpLabels.ProductUpdates:
              if (this.state.productUpdates) {
                this.update({showChangelogModal: true});
              }
              break;
            case HelpLabels.Docs:
              mixpanel.track(HelpTrackingEvent.Docs);
              break;
            case HelpLabels.AskQuestions:
            case HelpLabels.ContactSupport:
              this.openContactSupport();
              break;
            case HelpLabels.ContactSales:
              window.open(`https://mixpanel.com/contact-us/sales/`, `_blank`);
              mixpanel.track(HelpTrackingEvent.ContactSales);
              break;
            case HelpLabels.SubmitFeedback:
              promptFeedbackModal({
                feature: FeedbackForFeature.General,
              });
              break;
            case HelpLabels.LiveChat:
              this.hideAllWidgets();
              activateZendeskChat(ChatDepartment.Support, this.getCurrentUser()?.name, this.getCurrentUser()?.email);
              break;
            case HelpLabels.Intro:
              mixpanel.track(HelpTrackingEvent.Intro);
              promptIntroModal({'first-name': this.getFirstName()});
              break;
            case HelpGettingStartedLabels.SetupGuide:
              mixpanel.track(HelpTrackingEvent.SetupGuide);
              initImmersiveOnboardingInitializer();
              break;
            case HelpLabels.OnboardingGuide:
              mixpanel.track(HelpTrackingEvent.OnboardingGuide);
              this.update({dismissedOnboardingChecklist: false});
              unsetUserFlag(UserFlag.DismissedOnboardingChecklist);
              break;
            case HelpLabels.Community:
              mixpanel.track(HelpTrackingEvent.Community);
              break;
            case HelpLabels.HireAnExpert:
              mixpanel.track(HelpTrackingEvent.HireAnExpert);
              break;
            default:
              mixpanel.track(HelpTrackingEvent.ExternalLink, {
                'External Link Name': item.label,
                'External Link URL': item.href,
              });
              break;
          }
        },

        closeInviteModal: () => {
          this.helpers.updateOpen({inviteModal: false});
          this.state.closeInviteModalCallback?.();
        },

        showOnboardingChecklist: () => {
          if (
            this.params.headerMode === HeaderMode.Organization ||
            this.helpers.isDemoProject() ||
            this.state.isStarterBoardModalOpen
          ) {
            return false;
          }

          const queryParams = queryString.parse(getLocation().search);
          if (queryParams[`from_fyt`]) {
            return false;
          }

          return !this.state.dismissedOnboardingChecklist && this.state.enableOnboardingChecklist;
        },

        selectMenuChanged: (menuType: string, isOpen: boolean) => {
          if (menuType === `project` && isOpen) {
            this.update({projectMenuWorkspaces: this.getWorkspacesForProject(this.state.projectId)});
          }

          if (menuType === `help` && !isOpen) {
            this.update({isNewUser: false});
            this.markProductUpdatesAsSeen();
          }

          this.helpers.updateOpen({[`${menuType}Menu`]: isOpen});
          if (isOpen) {
            mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
              Menu: menuType,
              [SearchTrackedProperty.PhaseVersion]: this.getSearchPhaseVersion(),
              ...(menuType === `search` ? this.getSearchCommonProperties() : null),
            });
          }
        },

        selectCategory: (catName: string) => {
          if (catName) {
            mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
              Menu: catName,
              [SearchTrackedProperty.PhaseVersion]: this.getSearchPhaseVersion(),
            });
          }
          this.helpers.updateOpen({
            applicationsMenu: false,
            dataManagementMenu: false,
            projectMenu: false,
            setupMenu: false,
            userMenu: false,
          });
          this.update({selectedCategory: catName});
        },

        handleCategoryCloseMenu: (menu = ``) => {
          // race condition with toggleSelectedCategory, click twice to go to another menu rather than once
          if (menu) {
            this.update({selectedCategory: ``});
            this.helpers.updateOpen({[menu]: false});
          }
        },

        toggleSelectedCategory: (catName: string) => {
          // open and close menu if header is clicked
          const selectedCategory = this.state.selectedCategory !== catName ? catName : ``;
          this.helpers.selectCategory(selectedCategory);
        },

        handleClickReportName: (reportName: string, source: string) => {
          mixpanel.track(NavigationTrackingEvent.SelectReport, {
            'Nav Source': source,
            'Report From': this.state.currentReportName,
            'Report To': reportName,
          });
        },

        handleErrorToastClosed: () => {
          this.update({errorAlertMsg: null});
        },

        handleNotify: (ev) => {
          const {message} = ev.detail;
          this.addToast(message);
        },

        handleError: (ev) => {
          const {error, message} = ev.detail;
          this.showError(message || error.message, error);
        },

        handleLinkClick: (ev: MouseEvent) => {
          const target = ev.composedPath()[0] as HTMLElement;
          if (target && target.closest) {
            const anchorLink: HTMLAnchorElement = closestReportLink(target);
            // let browser handle open in new tab interaction for a(target=_blank)
            if (anchorLink && anchorLink.target !== `_blank`) {
              const href = anchorLink.getAttribute(`href`);
              this.startNavigationWorkflows(href);
              this.helpers.navigate(href, ev, {fromAnchorlink: true});
            }
          }
        },

        handleSwitchReport: (ev: CustomEvent) => {
          this.helpers.navigate(ev.detail.newUrlPath, ev, {
            keepReportContext: true,
          });
        },

        getNavReportsFromCategory: (category: NavCategoryType): NavReports => {
          const reportContext = this.helpers.getReportContext();
          return getNavReportsFromCategory(category, reportContext);
        },

        getReportContext: (): ReportContext => {
          const {projectMenuWorkspaces} = this.state;
          const workspaceId = this.currentWorkspaceId;
          const isProjectOrGlobalWorkspace = this.isProjectOrGlobalWorkspace(workspaceId, projectMenuWorkspaces);
          return {
            projectId: parseInt(getProjectId(), 10),
            project: this.getCurrentProject(),
            isProjectOrGlobalWorkspace,
          };
        },

        chromeHotKeySwitcher: (ev) => {
          return keyEventSwitcher(
            {
              [`${getCommandKey()} + KeyK`]: () => {
                mixpanel.track(NavigationTrackingEvent.OpenPrimaryMenu, {
                  Menu: `search`,
                  ...this.getSearchCommonProperties(),
                });
                this.helpers.handleOpenReportsAndDashboardClicked();
              },
            },
            {stopPropagation: true, usableInInputs: true},
          )(ev);
        },

        getSearchShortcutDisplayName: () => generateShortcutLabel(`K`, {modifiers: [getCommandKey()]}),

        handleSearchModalClose: () => {
          this.helpers.updateOpen({searchModal: false});
          this.update({searchedText: DEFAULT_SEARCH_QUERY_STRING});
        },

        getApplicationsMenuSections: (): Section[] => {
          const {projectId} = this.state;
          const workspaceId = this.currentWorkspaceId;

          const toSectionItem = (app: AppMenuApp) => ({
            label: app.name,
            subLabel: app.description,
            wrapSubLabel: true,
            href: app.url,
            isDisabled: app.isDisabled,
            tooltip: app.tooltipStr
              ? {
                  placement: Placement.Top,
                  text: app.tooltipStr,
                }
              : null,
          });

          const reportContext: ReportContext = this.helpers.getReportContext();
          const applicationReports = [ReportName.JqlConsole];
          const nonDashboardSupportedReports = [ReportName.Signal, ReportName.Impact, ReportName.ExperimentReporting];

          const allApplicationReports = [
            ...nonDashboardSupportedReports,
            ...(!this.helpers.isDemoProject() ? applicationReports : []),
          ].map((report): AppMenuApp => {
            const objReport = REPORT_INFO[report];
            const isDisabled = objReport.disabling?.isDisabled(reportContext);
            return {
              name: objReport.label,
              description: objReport.description,
              url: getReportUrl(objReport.endpoint(reportContext), {projectId, workspaceId}),
              hidden: objReport.hidden?.(reportContext),
              isDisabled,
              tooltipStr: isDisabled ? objReport.disabling.disabledMsg : null,
            };
          });

          const result: Section[] = [
            {
              label: this.t(`nav-header.mixpanel-apps`),
              items: allApplicationReports.filter((app) => !app.hidden).map(toSectionItem),
            },
          ];

          return result;
        },

        getSettingsMenuSections: (): Section[] => {
          const menuItems: SectionItem[] = [
            ...(this.state.projectId
              ? [
                  getOrgSettingsMenuItem({
                    currentUser: this.getCurrentUser(),
                    currentProject: this.getCurrentProject(),
                  }),
                  getProjectSettingsMenuItem({currentProject: this.getCurrentProject()}),
                ]
              : []),
            getAccountSettingsMenuItem({currentUser: this.getCurrentUser()}),
          ].filter(Boolean);

          // GOOT: logged in users are getting "Leave Demo"
          if (this.getCurrentUserMeta().status === ModelStatus.Loading) {
            return [
              this.getUserProfile(),
              this.getDarkModeSwitcher(),
              this.getLanguageSwitcher(),
              {
                items: menuItems,
              },
              {
                items: [
                  {
                    label: this.t(`settings.logout`),
                    value: `settings.logout`,
                  },
                ],
              },
            ];
          }

          if (this.isGuestUser) {
            return [
              this.getDarkModeSwitcher(),
              this.getLanguageSwitcher(),
              {
                items: menuItems,
              },
              {
                items: [
                  {
                    label: this.t(`settings.leave-demo`),
                    value: `settings.leave-demo`,
                  },
                ],
              },
            ];
          }

          return [
            this.getUserProfile(),
            this.getDarkModeSwitcher(),
            this.getLanguageSwitcher(),
            {
              items: menuItems,
            },
            this.getShortcutMenu(),
            this.getAdminOrgMenu(),
            this.determineStartupCreditPromoFeatureEligibility() && {
              items: this.createStartupCreditPromoSectionItem(),
            },
            {
              items: [{label: this.t(`settings.logout`), value: `settings.logout`}],
            },
          ].filter(Boolean);
        },

        handleToastCtaAction: (ev) => {
          ev.preventDefault();
          const selectedToastId = ev.target.dataset.id;
          if (selectedToastId && toastCtaCallbackMap.has(selectedToastId)) {
            toastCtaCallbackMap.get(selectedToastId).call(null);
            toastCtaCallbackMap.delete(selectedToastId);
          }
        },

        getUserHasCreateWorkspacePermission: () => {
          const projectId = this.state.projectMenuWorkspaces[0].projectId;
          const project = this.getProject(projectId);
          try {
            return (
              project &&
              this.orgHasPerm(this.getCurrentUser()?.organizations[project.organizationId], Permission.ManageWorkspaces)
            );
          } catch (err) {
            reporter.error(err, {projectId, project, orgIds: this.helpers.getOrgs().map((org) => org.id)});
            return false;
          }
        },

        handleCreateWorkspaceBtnClick: async () => {
          // grab this now because the menu can collapse behind the modal and will 'reset' to the current project based on attrs
          const projectId = this.state.projectMenuWorkspaces[0].projectId;
          try {
            const {didSave, name, description} = await promptManageWorkspaceModal(
              this.t(`settings.project-settings.data-views.create-new`),
              {},
              projectId,
            );
            if (didSave) {
              const workspace = await workspacesClient.createWorkspace(projectId, {name, description});
              this.getContext(`projects`).update({
                [projectId]: {
                  workspaces: {[workspace.id]: workspace},
                },
              });
              if (projectId === this.getCurrentProject()?.id) {
                this.getContext(`currentProject`).update({
                  workspaces: {[workspace.id]: workspace},
                });
              }
              if (this.state.projectMenuWorkspaces[0] && this.state.projectMenuWorkspaces[0].projectId === projectId) {
                // the already computed and sorted project menu needs to be updated with the new workspace
                this.update({projectMenuWorkspaces: this.getWorkspacesForProject(projectId)});
              }
              const [eventName, eventProps] = workspaceCreateEvent(
                this.getWorkspacesForProject(projectId).length,
                {
                  name,
                  description,
                  projectId,
                },
                UISourceObject.ChromeHeaderCreateWorkspace,
              );
              mixpanel.track(`${workspacesTrackingPrefix} ${eventName}`, eventProps);
              this.addToast(
                `Workspace ${name} Created`,
                ToastType.SUCCESS,
                `Manage`,
                null,
                `/report/${workspace.projectId}/settings/#project/${workspace.projectId}/views/detail/${workspace.id}`,
              );
            }
          } catch (err) {
            const [eventName, eventProps] = workspaceApiFailure(ApiFailureDescription.Creation, err.message, {
              projectId,
              source: UISourceObject.ChromeHeaderCreateWorkspace,
            });
            mixpanel.track(`${workspacesTrackingPrefix} ${eventName}`, eventProps);
            if (err.message && (err.message as string).includes(`already exists`)) {
              this.addToast(`Data View already exists`, ToastType.ERROR, `Manage Data Views`, () =>
                this.getUrlManager().setLocation(`/settings/project/${projectId}/views`),
              );
            } else {
              this.showError(`Data view creation failed. Please try again or contact support.`, err);
            }
          }
        },

        supportWidgetAttrs: (): SupportWidgetAttrs => {
          const projectDetails = this.getCurrentProject();
          const user = this.getCurrentUser() || userCookieUtils.getUserIdentityFromCookie();
          return {
            elref: `supportWidget`,
            'events-plan-id': projectDetails?.eventsPlanID,
            'people-plan-id': projectDetails?.peoplePlanID,
            'billing-account': supportInfoStore.value.billingAccount,
            'support-plan': supportInfoStore.value.supportPlan,
            'user-age-in-days': supportInfoStore.value.userAgeDays,
            'user-email': user.email,
            'user-id': user.id,
            'user-name': user.name,
            'project-id': this.state.projectId,
            'should-override-free-warning': this.helpers.isInFreeSupportExperiment(),
          };
        },

        isInFreeSupportExperiment: () => {
          return this.getCurrentProject()?.hasFeatureFlag(ProjectFeatureFlag.FreeSupportTrial);
        },

        hasChangelogModalFeatureFlag: () => {
          return this.getCurrentProject()?.hasFeatureFlag(ProjectFeatureFlag.ChangelogModal);
        },

        shouldShowHelpMenuNotif: (): boolean => {
          return this.state.isNewUser || !!this.getUnseenProductUpdates().length;
        },

        shouldShowSettingsMenuNotif: (): boolean => {
          const currentProject = this.getCurrentProject();
          const featureEligible = this.determineStartupCreditPromoFeatureEligibility();

          return currentProject
            ? currentProject.hasStartupCreditAndFreeUser() &&
                !this.state.viewedStartupCreditPromo &&
                this.state.viewedReportCount > 1 &&
                featureEligible
            : false;
        },
        getStartupCreditBalances: (): CreditBalanceInfo | undefined => {
          return this.getCurrentProject()?.getStartupCreditBalances();
        },

        getStartupCreditInfo: (): CreditInfo | undefined => {
          return this.getCurrentProject()?.getStartupCreditInfos()[0];
        },

        dismissOnboardingChecklist: (showTooltip = true): void => {
          setUserFlag(UserFlag.DismissedOnboardingChecklist);
          this.update({dismissedOnboardingChecklist: true});
          if (showTooltip) {
            this.update({showOnboardingChecklistHelpTooltip: true});
            window.setTimeout(() => {
              this.update({showOnboardingChecklistHelpTooltip: false});
            }, 2500);
          }
        },
        completeOnboardingChecklist: (): void => {
          this.update({dismissedOnboardingChecklist: true});
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  getLeftAvatarUrl() {
    const isHomeDisabled = this.getCurrentProject()?.hasFeatureFlag(ProjectFeatureFlag.DisableHome);

    if (this.params.headerMode === HeaderMode.Organization) {
      return `/`;
    }
    return isHomeDisabled ? this.getBrowseUrl({resetFilters: true}) : this.getReportUrl(`home`);
  }

  getReportUrl(endpoint: string) {
    return getReportUrl(endpoint, {projectId: this.state.projectId, workspaceId: this.currentWorkspaceId});
  }

  getBrowseUrl(options: GetBrowseUrlOptions) {
    return getBrowseUrl({
      ...options,
      projectId: this.state.projectId,
      workspaceId: this.currentWorkspaceId,
    });
  }

  private determineStartupCreditPromoFeatureEligibility(): boolean {
    const currentProject = this.getCurrentProject();
    if (!currentProject) {
      return false;
    }
    if (this.startupCreditPromoFeatureEligible !== null) {
      return this.startupCreditPromoFeatureEligible;
    }
    const featureEligible = currentProject
      ? featureSamplingUtils.determineFeatureEligibility(
          currentProject.organizationId?.toString(),
          `startup-credit-promo`,
          100,
        )
      : false;
    this.startupCreditPromoFeatureEligible = featureEligible;
    return featureEligible;
  }

  private getShortcutMenu() {
    if (this.params.headerMode === HeaderMode.Organization) {
      return null;
    }
    const currentProject = this.getCurrentProject();
    const currentOrganization = this.getCurrentUser()?.organizations[currentProject.organizationId];

    const shortcutItems: SectionItem[] = [
      {
        label: this.t(`settings.setup`),
        href: `?immersive-onboarding=${Step.Implement}`,
        value: `Setup Mixpanel`,
        tracking: SETTINGS_CATEGORY_TRACKING.GettingStarted,
      },
    ];

    if (currentOrganization?.permissions?.includes(Permission.ViewOrganizationSettings)) {
      shortcutItems.push({
        label: this.t(`settings.billing-plan`),
        value: `settings.billing-plan`,
        href: getReportUrl(`settings/#org/${currentOrganization.id}/plan`, {projectId: this.getCurrentProjectId()}),
        tracking: SETTINGS_CATEGORY_TRACKING.BillingDetails,
      });
    }

    if (currentOrganization?.permissions.includes(Permission.AddUser)) {
      shortcutItems.push({
        label: this.t(`settings.invite-users`),
        value: `settings.invite-users`,
      });
    }

    // boolean used for getting users back to FYT
    // for FYT users, will only have demo project org available
    const userIsFromFindYourTeam =
      this.getCurrentUserMeta().status === ModelStatus.Success &&
      this.helpers.getOrgs({excludeDemoOrgs: true}).length === 0;

    if (userIsFromFindYourTeam) {
      shortcutItems.push({
        label: this.t(`settings.back-to-signup`),
        icon: `arrow-left`,
        href: `/find-your-org`,
        tracking: events.clickBackToSignup,
      });
    }

    return {
      label: this.t(`settings.shortcuts`),
      items: shortcutItems,
    };
  }

  private getAdminOrgMenu() {
    const adminOrgs = Object.values(userDetailsStore.value?.organizations)
      .filter((org) => org.is_actionable_admin_org)
      .map((org) => ({id: org.id.toString(), name: org.name}));

    if (adminOrgs.length > 0) {
      return {
        label: this.t(`settings.admin-org-menu`),
        items: adminOrgs.map((org) => ({
          label: org.name,
          value: `settings.admin-org-menu`,
          href: `/organization/${org.id}/app/settings/overview`,
          tracking: SETTINGS_CATEGORY_TRACKING.AdminOrg,
        })),
      };
    }

    return null;
  }

  private getDarkModeSwitcher(): Section {
    const isDarkMode = this.getContext(`themes`).isDarkMode();
    return {
      actionsRenderer: () =>
        SwitchEl({
          attrs: {
            on: isDarkMode,
          },
        }),
      items: [
        {
          label: this.t(`common.dark-mode`),
          onSelect: () => {
            this.setDarkMode(!isDarkMode, {shouldPersist: true});
            return false;
          },
        },
      ],
    };
  }

  private getLanguageSwitcher(): Section {
    return {
      items: [
        {
          label: this.t(`settings.language`),
          subSections: getLocaleMenuSections({i18n: this.getContext(`i18n`)}),
        },
      ],
    };
  }

  private getUserProfile(): Section {
    const roleName = capitalize(this.getCurrentProject()?.role?.name || `----`);
    const profileName = this.getCurrentUser()?.name || `User`;

    return {
      iconRenderer: ({id, name}: SectionItem) =>
        UserIconEl({attrs: {id, name, 'disable-tooltip': true, size: IconSizeType.Medium}}),
      items: [
        {
          label: `${profileName}`,
          subLabel: roleName,
          labelTheme: LabelTheme.Strong,
          subLabelTheme: SubLabelTheme.Soft,
          id: this.getCurrentUser()?.id,
          name: profileName,
          isReadOnly: true,
          value: `Profile & Preferences`,
          tracking: SETTINGS_CATEGORY_TRACKING.Account,
        },
      ],
    };
  }

  private createStartupCreditPromoSectionItem(): SectionItem[] {
    const currentProject = this.getCurrentProject();
    const item = getStartupCreditPromoSectionItem({currentProject});
    return item ? [item] : [];
  }

  resourceCustomSubscriptions = new Map<typeof SubscribableContext, () => void>([
    [
      CurrentUserResource,
      () => {
        this.tryRedirectToWorkspace();
      },
    ],
    [
      ProjectsResource,
      () => {
        this.tryRedirectToWorkspace();
      },
    ],
    [
      CurrentProjectResource,
      () => {
        const currentProject = this.getCurrentProject();
        // if current project has workspaces but we don't have a workspaceId
        // set a workspaceId from mp_persistence cookie or the project's global workspace
        // and redirect the user to the url with this valid workspaceId
        const stateUpdate = {};
        // check if the current workspaceId is still valid. It can be invalid, if a user leaves a workspace
        // in data views library.
        if (currentProject) {
          this.update({categories: this.getCategories()});
          this.helpers.updateCurrentCategoryAndReportNameFromUrl();
          // If a staff user doesn't have access to any workspaces, we keep the workspace_id that was initialized
          // this prevents workspace_id from being changed as a staff user moves from app to app.
          if (this.getCurrentUser()?.isStaff && !Object.keys(currentProject.workspaces).length) {
            this.update(stateUpdate);
            return;
          }

          const currentWorkspaceId = this.currentWorkspaceId;
          const validWorkspaceId = this.findCurrentValidWorkspaceId(currentWorkspaceId);
          if (validWorkspaceId !== currentWorkspaceId && this.state.currentReportName) {
            const fragment = this.getUrlManager().getLocation().hash;
            const path = makePathFromUrlState({
              projectId: this.state.projectId.toString(),
              workspaceId: validWorkspaceId?.toString(),
            });

            const location = path + fragment;

            mixpanel.track(
              `Invalid workspace redirect`,
              {
                'Old workspace': currentWorkspaceId,
                'New workspace': validWorkspaceId,
              },
              () => {
                this.getUrlManager().setLocation(location);
              },
            );
          }

          // projectMenuWorkspaces
          const projectMenuWorkspaces = this.getWorkspacesForProject(currentProject.id);
          if (projectMenuWorkspaces.length) {
            Object.assign(stateUpdate, {projectMenuWorkspaces});
          }
        }

        if (!currentProject.isDemo) {
          this.lastUserProjectStorage.setItem({
            key: LAST_USER_PROJECT_KEY,
            userId: userCookieUtils.getUserIdentityFromCookie()?.id,
            value: {
              projectId: currentProject.id,
            },
          });
        }

        // the function should always run update()
        this.update(stateUpdate);

        this.helpers.initializeSpaNavManager();
      },
    ],
  ]);

  private tryRedirectToWorkspace() {
    const currentUser = this.getCurrentUser();
    const allProjects = this.getAllProjects();
    if (!currentUser || !allProjects) {
      return;
    }

    const isStaff = currentUser.isStaff;
    if (isStaff) {
      return;
    }
    const currentProjectId = parseInt(getProjectId(), 10);
    const currentWorkspaceId = this.currentWorkspaceId;
    const currentProject = allProjects.find((project) => project.id === currentProjectId);
    const currentWorkspace = allProjects
      .map((project) => Object.values(project.workspaces))
      .flat()
      .find((workspace) => workspace.id === currentWorkspaceId);
    // catches the case when the workspace is valid and the current user has access, but the project is invalid (for example, a project that's been reset)
    if (!currentProject && currentWorkspace) {
      const fragment = this.getUrlManager().getLocation().hash;
      const path = makePathFromUrlState({
        projectId: currentWorkspace.projectId.toString(),
        workspaceId: currentWorkspace.id.toString(),
      });

      const location = path + fragment;

      console.error(`Invalid project, redirecting to valid workspace`);
      mixpanel.track(
        `Invalid project redirect`,
        {
          'Old project': currentProjectId,
          'New project': currentWorkspace.projectId,
        },
        () => {
          this.getUrlManager().setLocation(location);
        },
      );
    }
  }

  setDarkMode(enabled: boolean, {shouldPersist = false}: {shouldPersist?: boolean} = {}) {
    mixpanel.track(events.userSetDarkMode, {enabled});
    this.getContext(`themes`).setDarkMode(enabled);

    if (shouldPersist) {
      this.userLocalStorageDarkModeHelper.setItem({
        key: DARK_MODE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
        value: {darkMode: enabled},
      });
    }
  }

  findCurrentValidWorkspaceId(firstPriorityWorkspaceId: number | null): number | null {
    const currentProject = this.getCurrentProject();
    if (!currentProject) {
      return null;
    }
    return this.getValidWorkspaceId(currentProject, [
      firstPriorityWorkspaceId,
      this.getWorkspaceIdFromPersistenceCookie(currentProject.id),
      this.getGlobalWorkspaceIdForProject(currentProject.id),
    ]);
  }

  getValidWorkspaceId(project: Project, workspaceIdPriorityList: number[]) {
    const userWorkspaces = this.getWorkspacesForProject(project ? project.id : this.state.projectId) || [];
    for (const workspaceId of workspaceIdPriorityList) {
      const found = userWorkspaces.find((workspace) => workspace.id === workspaceId);
      if (found) {
        return found.id;
      }
    }
    return userWorkspaces.length ? userWorkspaces[0].id : null;
  }

  handleSupportInfo() {
    if (!this.isGlobalSidenavEnabled()) {
      activatePersistedZendeskChat(ChatDepartment.Support, this.getCurrentUser()?.name, this.getCurrentUser()?.email);
    }
    this.update();
  }

  public connectedCallback() {
    super.connectedCallback();
    this.initialize = this.initialize.bind(this);
    let isInitialized = false;
    // projectId and workspaceId is present on url 99% of the time but for some url e.g settings, it's not.
    // we have to wait for DOMContentLoaded for those cases
    if (getProjectId()) {
      this.initialize();
      isInitialized = true;
    }
    const searchParams = new URLSearchParams(getLocation().search);
    if (searchParams.has(`contact-us`) && searchParams.get(`contact-us`) !== `false`) {
      this.update({
        isSupportWidgetMaximized: true,
        showSupportWidget: true,
      });
    }

    if (searchParams.has(`submit-feedback`) && searchParams.get(`submit-feedback`) !== `false`) {
      promptFeedbackModal({
        feature: FeedbackForFeature.General,
      });
    }

    if (searchParams.has(IMMERSIVE_ONBOARDING_URL_PARAM)) {
      this.update({
        enableImmersiveOnboarding: true,
      });
    }

    this.userLocalStorageDarkModeHelper = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.DarkMode,
    });

    this.lastUserProjectStorage = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.LastUserProject,
    });

    // mp.report.globals are initialized after chrome-header is connected
    // so we need to wait for DOMContentLoaded
    window.addEventListener(
      `DOMContentLoaded`,
      () => {
        if (!isInitialized) {
          this.initialize();
        }
      },
      {once: true},
    );

    document.addEventListener(`openContactSupport`, this.openContactSupport);
    // hack to make fake currentProjectResource callback to be always fired when page changed
    if (this.getCurrentProject()) {
      this.getContext(`currentProject`).update(this.getCurrentProject());
    }
  }

  public initialize() {
    const projectId = parseInt(getProjectId(), 10);
    const location = this.getUrlManager().getLocation();
    this.update({projectId, currentUrlPath: location.pathname});

    // clear out volt shell elements if any. mp-chrome-header's #shadow-root is unaffected
    this.innerHTML = ``;

    this.handleSupportInfo = this.handleSupportInfo.bind(this);

    useStore(this, supportInfoStore, this.handleSupportInfo).fetch();

    useStore(this, userDetailsStore).fetch();

    // projectId could be empty if user does not have access to any project
    // see https://mixpanel.slack.com/archives/C014L0QMH1T/p1592860852025000
    if (projectId) {
      // TODO remove this once we're sure no other apps/components are relying on
      // chrome-header initiating this fetch for them...
      currentProjectStore.fetch();
    } else {
      reporter.info(`Cannot fetch current project id`, {url: location.href});
    }

    const projectMenuWorkspaces = this.getWorkspacesForProject(projectId);
    this.update({projectMenuWorkspaces});

    this.helpers.initializeSpaNavManager();

    window.addEventListener(`popstate`, this.helpers.handleHistoryStateChange);
    window.addEventListener(`pushstate`, this.helpers.handleHistoryStateChange);
    window.addEventListener(`replacestate`, this.helpers.handleHistoryStateChange);
    document.addEventListener(`click`, this.helpers.handleLinkClick);
    document.addEventListener(`reportswitch`, this.helpers.handleSwitchReport);
    this.el.addEventListener(`click`, this.helpers.handleLinkClick); // catch link clicks within shadow dom

    this.setStateByUserFlagsValueAndPromptModals();
    this.getProductUpdates();
  }

  public disconnectedCallback() {
    window.removeEventListener(`popstate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`pushstate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`replacestate`, this.helpers.handleHistoryStateChange);
    document.removeEventListener(`click`, this.helpers.handleLinkClick);
    document.removeEventListener(`reportswitch`, this.helpers.handleSwitchReport);
    this.el.removeEventListener(`click`, this.helpers.handleLinkClick);
    document.removeEventListener(`keydown`, this.helpers.chromeHotKeySwitcher);
    document.removeEventListener(`openContactSupport`, this.openContactSupport);

    super.disconnectedCallback();
  }

  getCurrentProject(): CurrentProject | undefined {
    return this.getCurrentProjectMeta()?.value;
  }

  isGlobalSidenavEnabled(): boolean {
    return isGlobalSidenavEnabled(this.getCurrentProject());
  }

  getCurrentProjectId() {
    return Number(this.getContext(`currentProject`).id);
  }

  getCurrentProjectMeta(): Model<CurrentProject> {
    if (this.params.headerMode === HeaderMode.Organization) {
      return null;
    }
    return this.getContext(`currentProject`).get();
  }

  getProjectMenuLabel() {
    if (this.params.headerMode === HeaderMode.Organization) {
      return `Projects`;
    }
    return this.getCurrentProjectName();
  }

  getCurrentProjectName() {
    return !this.state.projectId || this.getCurrentProjectMeta().status === ModelStatus.Loading
      ? `Loading ...`
      : this.getCurrentProject().name;
  }

  getCurrentUser(): CurrentUser {
    return this.getCurrentUserMeta().value;
  }

  getCurrentUserMeta(): Model<CurrentUser> {
    return this.getContext(`currentUser`).get();
  }

  getProject(projectId): Project {
    return this.getContext(`projects`).getOne(projectId).value;
  }

  get isGuestUser() {
    return this.getCurrentUserMeta().status === ModelStatus.Success && this.getCurrentUser().isGuest;
  }

  get currentWorkspaceId(): number | null {
    // rely on the url for the current workspace id
    let currentWorkspaceId = parseInt(getWorkspaceId(this.getUrlManager()), 10) || null; // null instead of NaN

    // project only apps never have the workspace id in the url; for these apps we add this special logic to
    // obtain a valid workspace id from outside the url
    if (this.getCurrentProject() && isProjectOnlyApp(this.state.currentReportName)) {
      currentWorkspaceId = this.findCurrentValidWorkspaceId(currentWorkspaceId);
    }

    return currentWorkspaceId;
  }

  public isDownloadMode(): boolean {
    return getDownloadMode(this.getUrlManager()) !== DownloadMode.None;
  }

  private getCategories(): NavCategories {
    return getNavCategories({reportContext: this.helpers.getReportContext()});
  }

  private getWorkspacesForProject(projectId: number): WorkspaceObj[] {
    const project = projectId === this.getCurrentProjectId() ? this.getCurrentProject() : this.getProject(projectId);
    return Object.values(project?.workspaces ?? {}).sort(sortWorkspaces);
  }

  private isProjectOrGlobalWorkspace(workspaceId: number, projectMenuWorkspaces: WorkspaceObj[]) {
    if (projectMenuWorkspaces) {
      const workspace = projectMenuWorkspaces.find((ws) => ws.id === workspaceId);
      return !workspace || workspace.isGlobal;
    }
    return true;
  }

  private orgHasPerm(org: Organization, perm: string) {
    if (!org || !perm) {
      return false;
    } else {
      return Array.isArray(org.permissions) && org.permissions.includes(perm);
    }
  }

  private trackSettingsMenuItemSelected(item) {
    const {org, tracking} = item;

    if (tracking) {
      if (tracking === events.viewOrg && org) {
        track(tracking, {
          [properties.orgId]: org.id,
          [properties.orgName]: org.name,
        });
      } else {
        track(tracking);
      }
    }
  }

  getUrlManager() {
    return this.getContext(`urlManager`);
  }

  private addToast(message: string, toastType?: ToastType, ctaLabel?, ctaCallback?, ctaHref?) {
    const newToast = {message, ttl: TOAST_TTL, type: toastType, cta: ctaLabel, ctaCallback, ctaHref};
    addToast(newToast);
  }

  private showError(errorAlertMsg: string, err: Error) {
    if (err) {
      console.error(err);
    }
    this.update({errorAlertMsg});
  }

  private getReportInfoFromUrl(url: URL): {categoryName: string; reportName: string; reportEndpoint: string} | null {
    // go through every report and see if we have an endpoint match
    for (const [categoryName, category] of Object.entries(this.state.categories || {})) {
      for (const [reportName, report] of Object.entries(category.reports)) {
        const [endpointPath, endpointHash] = report.endpoint.split(`#`);

        // allow trailing hash in endpoint match
        const hasEndpointMatch =
          url.pathname.endsWith(`/${endpointPath}`) ||
          url.pathname.endsWith(`/${endpointPath}/`) ||
          report.endpointAliases?.some(
            (alias: string) => url.pathname.endsWith(`/${alias}`) || url.pathname.endsWith(`/${alias}/`),
          );
        const hasHashMatch = !endpointHash || url.hash.startsWith(`#${endpointHash}`);
        if (hasEndpointMatch && hasHashMatch) {
          return {
            categoryName,
            reportName,
            reportEndpoint: report.endpoint,
          };
        }
      }
    }

    // if endpoint isn't found in categories tree
    const reportEndpoint = parseStateFromUrl(url.pathname)?.appName;
    if (reportEndpoint) {
      return {
        categoryName: ``,
        reportName: reportEndpoint,
        reportEndpoint,
      };
    }

    return null;
  }

  private async persistLastSeenUrlData(): Promise<void> {
    const location = this.getUrlManager().getLocation();
    const url = location.href;
    const reportInfo = this.getReportInfoFromUrl(new URL(url));
    if (!isPersistableReport()) {
      debug.warn(`Current report not persistable`);
    } else if (!reportInfo) {
      reporter.warn(`Report info not found for given URL`, {url});
    } else {
      // currentProject must be set before we can get the actual valid workspace id
      if (this.getCurrentProject()) {
        // we need to check the the current workspace id is equal to the valid workspace id
        // or we run the risk of setting the wrong workspace id in the cookie
        let workspaceId = this.currentWorkspaceId;
        const validWorkspaceId = this.findCurrentValidWorkspaceId(workspaceId);
        if (validWorkspaceId !== workspaceId) {
          workspaceId = validWorkspaceId;
        }
        await safeSetCookie(
          REPORT_PERSISTENCE_COOKIE,
          JSON.stringify({
            project: this.state.projectId,
            ...(workspaceId ? {workspace: workspaceId} : {}),
            report_path: reportInfo.reportEndpoint,
          } as ReportPersistenceCookie),
          14 /*days*/,
        );
      }
    }
  }

  private getWorkspaceIdFromPersistenceCookie(projectId: number): number | null {
    try {
      const persistenceCookie: ReportPersistenceCookie = JSON.parse(getCookie(REPORT_PERSISTENCE_COOKIE) || `{}`);
      if (persistenceCookie.workspace && persistenceCookie.project === projectId) {
        return persistenceCookie.workspace;
      }
    } catch {
      /* fail gracefully*/
    }
    return null;
  }

  private getGlobalWorkspaceIdForProject(projectId: number): number | null {
    const projectWorkspaces = this.getWorkspacesForProject(projectId);
    const globalWorkspace = projectWorkspaces.find((workspace) => workspace.isGlobal);
    return globalWorkspace ? globalWorkspace.id : null;
  }

  private hideAllWidgets() {
    this.update({showSupportWidget: false});
    hideZendeskWidget();
  }

  private getSearchCommonProperties() {
    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.Source]: SearchSource.Dropdown,
      [SearchTrackedProperty.ContentDiscoveryType]: ContentDiscoveryType.Browse,
    };
  }

  private getSearchPhaseVersion() {
    return SearchPhaseVersion.AddedUniqueViewsCustomRank;
  }

  private async setStateByUserFlagsValueAndPromptModals() {
    const flags = await getUserFlags();
    const isNewUser = !(UserFlag.ViewedReportCount in flags);
    const viewedReportCount = flags.VIEWED_REPORT_COUNT || 0;
    const viewedStartupCreditPromo = UserFlag.ViewedStartupCreditPromo in flags;
    const dismissedOnboardingChecklist = UserFlag.DismissedOnboardingChecklist in flags;
    const enableOnboardingChecklist = UserFlag.EnableOnboardingChecklist in flags;

    this.update({
      isNewUser,
      viewedReportCount,
      viewedStartupCreditPromo,
      dismissedOnboardingChecklist,
      enableOnboardingChecklist,
    });

    viewedReportFlagManager.increment();

    const showFYTModal =
      !(UserFlag.ViewedFYTModal in flags) &&
      userDetailsStore.value?.organizations?.[this.getCurrentProject()?.organizationId]?.should_show_fyt_modal;

    if (showFYTModal) {
      this.setViewedFYTModal();
      await promptFYTModal();
    }
  }

  public setViewedBenchmarkAnnouncement = () => {
    incrementUserFlag(UserFlag.ViewedBenchmarkAnnouncementCount);
  };

  public setViewedStartupCreditPromo = () => {
    setUserFlag(UserFlag.ViewedStartupCreditPromo);
    userFlagsStore.update({[UserFlag.ViewedStartupCreditPromo]: 1});
    this.update({viewedStartupCreditPromo: true});
  };

  public setViewedFYTModal = () => {
    setUserFlag(UserFlag.ViewedFYTModal);
    userFlagsStore.update({[UserFlag.ViewedFYTModal]: 1});
  };

  private getFirstName() {
    return (this.getCurrentUser()?.name || ``).split(` `)[0];
  }

  private getAllProjects() {
    return this.getContext(`projects`).getAllValues();
  }

  private async getProductUpdates() {
    const productUpdates = await getProductUpdates();
    this.update({productUpdates});
  }

  private getUnseenProductUpdates() {
    return this.state.productUpdates.filter((update) => !update.seen);
  }

  private markProductUpdatesAsSeen() {
    const {productUpdates} = this.state;
    const unseen = this.getUnseenProductUpdates();

    if (unseen.length) {
      markProductUpdatesAsSeen(unseen.map(({id}) => id));
      this.update({
        productUpdates: productUpdates.map((update) => ({...update, seen: true})),
      });
    }
  }

  private startNavigationWorkflows(urlToNavigate: string) {
    if (this.state.currentReportName === ReportName.Dashboards && isSavedCoreReportUrl(urlToNavigate)) {
      captureWorkflowTiming(this, {
        name: `Dashboards => Core Report Query`,
        key: `navigation-to-saved-report-query-${urlToNavigate}`,
        isComplete: (ev) => ev.detail.eventName.includes(QueryStartEvent),
        additionalProps: {tab: parseStateFromUrl(urlToNavigate).appName},
      });
    }
  }

  openContactSupport = () => {
    mixpanel.track(HelpTrackingEvent.Support);
    this.hideAllWidgets();
    this.update({showSupportWidget: true});
  };
}

export const ChromeHeaderEl = defineAndMakeJsxFactory<JsxProps>(`mp-chrome-header`, ChromeHeader);
export const ChromeHeaderReact = panel2react<JsxProps>(`mp-chrome-header`, ChromeHeader);
